import { useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuthStore, useUIStore } from '@/store';
import { authApi } from '@/api';
import { LoginCredentials, SignUpData, ForgotPasswordData, ResetPasswordData, User } from '@/types';
import { ROUTES, DEFAULT_AUTHENTICATED_ROUTE } from '@/router';

/**
 * Authentication hook
 * Provides login, logout, signup, and password reset functionality
 */
export const useAuth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const setUser = useAuthStore((state) => state.setUser);
  const logout = useAuthStore((state) => state.logout);
  const setAuthError = useAuthStore((state) => state.setError);
  const clearAuthError = useAuthStore((state) => state.clearError);
  const showLoader = useUIStore((state) => state.showLoader);
  const hideLoader = useUIStore((state) => state.hideLoader);

  /**
   * Handle login
   */
  const handleLogin = useCallback(
    async (credentials: LoginCredentials) => {
      setIsSubmitting(true);
      clearAuthError();
      showLoader('Signing in...');

      try {
        const response = await authApi.login(credentials);

        // Extract user data and token from response
        const loginDetails = response.loginDetails;
        // Token is nested: loginDetails.token.authToken
        const token = typeof loginDetails.token === 'object'
          ? (loginDetails.token as any).authToken
          : loginDetails.token;

        const user: User = {
          userId: (loginDetails as any).userId,
          userEmail: (loginDetails as any).userEmail || credentials.userEmail,
          userName: loginDetails.userName || (loginDetails as any).firstName || '',
          userRole: (loginDetails as any).isAdmin === true || (loginDetails as any).isAdmin === 'true' ? 'admin' : ((loginDetails as any).role || 'user'),
          token: token,
        };

        setUser(user, token);

        toast.success('Successfully logged in!');

        // Redirect to return URL or dashboard
        const returnUrl = (location.state as { returnUrl?: string })?.returnUrl;
        navigate(returnUrl || DEFAULT_AUTHENTICATED_ROUTE, { replace: true });

        return true;
      } catch (error: any) {
        const message = error.message || 'Login failed. Please check your credentials.';
        setAuthError(message);
        toast.error(message);
        return false;
      } finally {
        setIsSubmitting(false);
        hideLoader();
      }
    },
    [navigate, location.state, setUser, setAuthError, clearAuthError, showLoader, hideLoader]
  );

  /**
   * Handle signup
   */
  const handleSignUp = useCallback(
    async (data: SignUpData) => {
      setIsSubmitting(true);
      clearAuthError();
      showLoader('Creating account...');

      try {
        await authApi.signUp(data);

        toast.success('Account created successfully! Please log in.');
        navigate(ROUTES.LOGIN);

        return true;
      } catch (error: any) {
        const message = error.message || 'Sign up failed. Please try again.';
        setAuthError(message);
        toast.error(message);
        return false;
      } finally {
        setIsSubmitting(false);
        hideLoader();
      }
    },
    [navigate, setAuthError, clearAuthError, showLoader, hideLoader]
  );

  /**
   * Handle forgot password
   */
  const handleForgotPassword = useCallback(
    async (data: ForgotPasswordData) => {
      setIsSubmitting(true);
      clearAuthError();
      showLoader('Sending reset link...');

      try {
        await authApi.forgotPassword(data);

        toast.success('Password reset link sent to your email.');
        return true;
      } catch (error: any) {
        const message = error.message || 'Failed to send reset link. Please try again.';
        setAuthError(message);
        toast.error(message);
        return false;
      } finally {
        setIsSubmitting(false);
        hideLoader();
      }
    },
    [setAuthError, clearAuthError, showLoader, hideLoader]
  );

  /**
   * Handle reset password
   */
  const handleResetPassword = useCallback(
    async (data: ResetPasswordData) => {
      setIsSubmitting(true);
      clearAuthError();
      showLoader('Resetting password...');

      try {
        await authApi.resetPassword(data);

        toast.success('Password reset successfully! Please log in with your new password.');
        navigate(ROUTES.LOGIN);

        return true;
      } catch (error: any) {
        const message = error.message || 'Failed to reset password. Please try again.';
        setAuthError(message);
        toast.error(message);
        return false;
      } finally {
        setIsSubmitting(false);
        hideLoader();
      }
    },
    [navigate, setAuthError, clearAuthError, showLoader, hideLoader]
  );

  /**
   * Handle logout
   */
  const handleLogout = useCallback(() => {
    logout();
    // Clear all cached data to prevent leaking between users
    sessionStorage.clear();
    // Force page reload to reset all Zustand stores
    toast.info('You have been logged out.');
    window.location.href = '/login';
  }, [logout]);

  return {
    isSubmitting,
    handleLogin,
    handleSignUp,
    handleForgotPassword,
    handleResetPassword,
    handleLogout,
  };
};

export default useAuth;
